import { getApiBase } from "./apiBase";

const API_BASE = getApiBase({ trailingSlash: true });
const PENDING_REFERRAL_CODE_KEY = "qubiq_pending_referral_code";
export const OPEN_INVITE_FRIENDS_PROMPT_EVENT = "qubiq:open-invite-friends-prompt";

function normalizeReferralCode(value) {
  return String(value || "").trim().slice(0, 32);
}

export function setPendingReferralCode(code) {
  const normalizedCode = normalizeReferralCode(code);
  if (!normalizedCode || typeof window === "undefined") return;
  window.localStorage.setItem(PENDING_REFERRAL_CODE_KEY, normalizedCode);
}

export function getPendingReferralCode() {
  if (typeof window === "undefined") return "";
  return normalizeReferralCode(window.localStorage.getItem(PENDING_REFERRAL_CODE_KEY));
}

export function clearPendingReferralCode() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(PENDING_REFERRAL_CODE_KEY);
}

export function openInviteFriendsPrompt() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_INVITE_FRIENDS_PROMPT_EVENT));
}

export function buildInviteLink(invitePath) {
  const normalizedPath = String(invitePath || "").trim();
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://qubiq.games";
  return normalizedPath ? `${origin}${normalizedPath}` : origin;
}

export function buildInviteMessage(inviteLink) {
  return `Je t’invite sur Qubiq. Inscris-toi avec mon lien ${inviteLink} et on gagne 20 qubs chacun.`;
}

export async function copyInviteText(message) {
  const normalizedMessage = String(message || "").trim();
  if (!normalizedMessage) return false;

  if (
    typeof navigator !== "undefined" &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === "function"
  ) {
    await navigator.clipboard.writeText(normalizedMessage);
    return true;
  }

  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.value = normalizedMessage;
    textarea.setAttribute("readonly", "true");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    textarea.style.pointerEvents = "none";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      const copied = document.execCommand("copy");
      document.body.removeChild(textarea);
      return copied;
    } catch {
      document.body.removeChild(textarea);
    }
  }

  return false;
}

function getSmsRecipientsFromContacts(contacts) {
  const uniqueRecipients = new Set();

  for (const contact of Array.isArray(contacts) ? contacts : []) {
    const phoneValues = Array.isArray(contact?.tel) ? contact.tel : [];
    for (const value of phoneValues) {
      const normalizedValue = String(value || "").trim();
      if (!normalizedValue) continue;
      uniqueRecipients.add(normalizedValue);
    }
  }

  return Array.from(uniqueRecipients);
}

function openSmsComposer(recipients, message) {
  const recipientList = Array.isArray(recipients) ? recipients.filter(Boolean).join(",") : "";
  const body = encodeURIComponent(String(message || "").trim());
  const href = recipientList ? `sms:${recipientList}?body=${body}` : `sms:?body=${body}`;
  window.location.href = href;
}

export async function fetchInviteFriendsInfo(getToken) {
  const token = await getToken?.().catch(() => null);
  const response = await fetch(`${API_BASE}referrals/me`, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || "referrals_me_failed");
  }
  return data;
}

export async function consumeInvitePrompt(getToken) {
  const token = await getToken?.().catch(() => null);
  const response = await fetch(`${API_BASE}referrals/prompt/consume`, {
    method: "POST",
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || "referrals_prompt_consume_failed");
  }
  return data;
}

export async function claimPendingReferral(getToken, clientId, pendingReferralCode) {
  const normalizedCode = normalizeReferralCode(pendingReferralCode);
  if (!normalizedCode) {
    return { processed: false, applied: false, reason: "missing_code" };
  }

  const token = await getToken?.().catch(() => null);
  const response = await fetch(`${API_BASE}referrals/claim-pending`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(clientId ? { "x-client-id": clientId } : {}),
    },
    body: JSON.stringify({
      clientId,
      pendingReferralCode: normalizedCode,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || "referrals_claim_pending_failed");
  }
  return data;
}

export async function startInviteFriendsFlow({ inviteLink, onCopied }) {
  const normalizedLink = String(inviteLink || "").trim();
  if (!normalizedLink) {
    throw new Error("invite_link_missing");
  }

  const message = buildInviteMessage(normalizedLink);

  if (
    typeof navigator !== "undefined" &&
    navigator.contacts &&
    typeof navigator.contacts.select === "function"
  ) {
    try {
      const contacts = await navigator.contacts.select(["name", "tel"], {
        multiple: true,
      });
      const recipients = getSmsRecipientsFromContacts(contacts);
      if (recipients.length) {
        openSmsComposer(recipients, message);
        return { method: "sms", recipientCount: recipients.length };
      }
    } catch (error) {
      if (error?.name === "AbortError") {
        return { method: "cancelled" };
      }
    }
  }

  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share({
        text: message,
        url: normalizedLink,
      });
      return { method: "share" };
    } catch (error) {
      if (error?.name === "AbortError") {
        return { method: "cancelled" };
      }
    }
  }

  if (await copyInviteText(message)) {
    onCopied?.();
    return { method: "copy" };
  }

  openSmsComposer([], message);
  return { method: "sms" };
}
